import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { addLabelToAirport, IFlightSchedules } from "./store"
import { ILocation } from "../Locations"
import { ICity, ICountryState, TTimezone } from "../Locations/store"
import * as Validator from "yup"
import { Fragment, useRef, useState } from "react"
import {
	SelectWorldCities,
	SelectWorldCountries,
	SelectWorldStates,
	TWorldAirline,
} from "../World"
import {
	AsyncSelect,
	Box,
	Button,
	Divider,
	Inline,
	Stack,
	Text,
} from "@sembark-travel/ui/base"
import { Dialog } from "@sembark-travel/ui/dialog"
import { AirLineForm } from "../Admin/World"
import {
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { Optional } from "utility-types"

function XHR(xhr: XHRInstance) {
	return {
		async getFlightDetails(flights: unknown): Promise<Array<IFlightSchedules>> {
			return xhr
				.get("/flight-schedules", { params: { flights } })
				.then((resp) => resp.data.data)
		},
		async getAirline(params?: unknown): Promise<IFlightSchedules["flight"][]> {
			return xhr.get("/flight", { params }).then(({ data }) => [data.flight])
		},
		async getAirports(params?: unknown): Promise<ILocation[]> {
			return xhr
				.get("/world/airports", { params })
				.then(({ data }) => data.data)
		},
		async getTimezones(params?: unknown): Promise<TTimezone[]> {
			return xhr
				.get("/world/timezones", { params })
				.then(({ data }) => data.data)
		},
		async getAdditionalInfo(type: string, params?: unknown): Promise<string[]> {
			return xhr
				.get(`flight-info-values/${type}`, { params: { q: params } })
				.then(({ data }) => data.data)
		},
	}
}
export function SelectAirlines({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const promiseOfCreation = useRef<
		((airport: TWorldAirline) => void) | undefined
	>(undefined)
	return (
		<Fragment>
			<AsyncSelect
				clearable
				placeholder="eg. AI-1234"
				{...props}
				labelKey="number_plate_formatted"
				optionRenderer={({ option, created }) =>
					created ? (
						<Box>Add "{option.name}"</Box>
					) : (
						<Stack gap={"1"}>
							<Text>{option.number_plate_formatted}</Text>
							<Text fontSize="sm" color="muted">
								{option.airline.name}
							</Text>
						</Stack>
					)
				}
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				//We are not receiving the id field from the API response, so we are currently using number_plate + index as the identifier.
				// When creating a new airline, we also use number_plate as the id.
				// To differentiate between creating a new airline and backend airline data, we are appending an index to the id for backend airline data

				fetch={(q) =>
					q.length > 2 // ita code is 2 chars long
						? XHR(xhr)
								.getAirline({ q })
								.then((resp) => {
									return resp.map((a, index) => ({
										id: `${a.number_plate}-${index}`,
										...a,
									}))
								})
						: Promise.resolve([])
				}
			/>
			<AddAirlineDialogBox
				open={!!newName}
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(airline) => {
					promiseOfCreation.current?.(airline)
				}}
			/>
		</Fragment>
	)
}
export function SelectTimezones({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			placeholder="Type to search timezone ..."
			{...props}
			labelKey="name"
			fetch={(q) =>
				XHR(xhr)
					.getTimezones({ q })
					.then((resp) => resp)
			}
		/>
	)
}
export function SelectAirports(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	const promiseOfCreation = useRef<((airport: ILocation) => void) | undefined>(
		undefined
	)
	return (
		<Fragment>
			<AsyncSelect
				{...props}
				fetchOnMount
				labelKey="label"
				placeholder="eg. BOM "
				optionRenderer={({ option, created }) =>
					created ? (
						<Box>Add "{option.name}"</Box>
					) : (
						<Stack gap={"1"}>
							<Inline alignItems={"center"} justifyContent={"between"}>
								<Text>
									{option?.city?.name} {"  "}
									{option?.country?.name}
								</Text>
								<Text>{option?.iata || "N/A"}</Text>
							</Inline>
							<Text fontSize="sm" color="muted">
								{option.name}
							</Text>
						</Stack>
					)
				}
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				fetch={(q) =>
					q.trim() !== ""
						? XHR(xhr)
								.getAirports({ q })
								.then((resp) => resp.map((l) => addLabelToAirport(l)))
						: Promise.resolve([])
				}
			/>
			<AddAirportDialogBox
				open={!!newName}
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(airport) => {
					promiseOfCreation.current?.(airport)
				}}
			/>
		</Fragment>
	)
}
export function SelectAdditionalFlightInfo({
	infoKey,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	infoKey: string
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			creatable
			{...props}
			labelKey="value"
			fetch={(q) =>
				XHR(xhr)
					.getAdditionalInfo(infoKey, q)
					.then((resp) => {
						return resp
					})
			}
		/>
	)
}

/**
 * add new airline
 */

function AddAirlineDialogBox({
	open,
	onClose,
	onSuccess,
	...props
}: Optional<
	Omit<React.ComponentProps<typeof AirLineForm>, "onCancel" | "onSuccess">
> & {
	open: boolean
	onClose: () => void
	onSuccess?: (airline: TWorldAirline) => void
}) {
	const xhr = useXHR()
	return (
		<Dialog open={open} onClose={onClose} sm>
			<Dialog.Header closeButton>
				<Dialog.Title>Add New Airline </Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<AirLineForm
					onSubmit={async (val) => {
						const {
							data: { data: airline },
						} = await xhr.post<{ data: TWorldAirline }>("/world/airlines", {
							...val,
						})
						onClose()
						onSuccess?.(airline)
					}}
					onCancel={onClose}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

/**
 *
 * add new airport
 */
export function AddAirportDialogBox({
	open,
	onClose,
	onSuccess,
	...props
}: Optional<
	Omit<React.ComponentProps<typeof NewAirportForm>, "onCancel">,
	"onSuccess"
> & {
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose}>
			<Dialog.Header closeButton>
				<Dialog.Title>Add New Airport</Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<NewAirportForm
					onSuccess={(...args) => {
						onClose()
						onSuccess?.(...args)
					}}
					onCancel={onClose}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}
const airportValidationSchema = validateFormValues(
	Validator.object().shape({
		name: Validator.string().required("Airport name is required"),
		iata: Validator.string()
			.required("Iata code is required")
			.length(3, "Iata code should be of 3 char"),
		timezone: Validator.object().required("Timezone is required "),
	})
)
export function NewAirportForm({
	initialValues: propInitialValues = {},
	onSuccess,
	onCancel,
}: {
	initialValues?: Optional<ILocation>
	onCancel?: () => void
	onSuccess: (airportLocation: ILocation) => void
}) {
	const xhr = useXHR()
	return (
		<Form<ILocation>
			validate={airportValidationSchema}
			onSubmit={withServerErrors(async (val) => {
				const { timezone, city, state, country, ...otherValues } = val
				const {
					data: { data: airport },
				} = await xhr.post<{ data: ILocation }>("/world/airports", {
					...otherValues,
					timezone: timezone?.id,
					city: city?.id,
					state: state?.id,
					country: country?.id,
				})
				onSuccess(addLabelToAirport(airport))
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap={"4"}>
						<Inline gap={"4"} flexWrap={"wrap"}>
							<TextInputField
								name="name"
								type="text"
								placeholder="eg. Dubai International Airport"
								label={"Airport Name"}
							/>
							<TextInputField
								name="iata"
								type="text"
								placeholder="eg. DXB"
								label={"Iata Code"}
							/>
							<SelectField
								creatable={false}
								select={SelectTimezones}
								name={`timezone`}
								label="Timezone"
								multiple={false}
								maxWidth="xs"
							/>
						</Inline>
						<Inline gap={"4"} flexWrap={"wrap"}>
							<SelectField
								select={SelectWorldCities}
								name={`city`}
								label="City"
								multiple={false}
								onChange={(value?: ICity) => {
									form.change(`city`, value)
									if (value?.state) {
										form.change(`state`, value.state)
										if (value.state.country) {
											form.change(`country`, value.state.country)
										}
									}
								}}
							/>
							<SelectField
								select={SelectWorldStates}
								name={`state`}
								label="State "
								multiple={false}
								onChange={(value?: ICountryState) => {
									form.change(`state`, value)
									if (value?.country) {
										form.change(`country`, value.country)
									}
								}}
							/>
							<SelectField
								select={SelectWorldCountries}
								name={`country`}
								label="Country"
								multiple={false}
								maxWidth="xs"
							/>
						</Inline>
						<SubmissionError />
					</Stack>
					<Divider sm />
					<Button type="submit" disabled={submitting} level="primary">
						{submitting ? "Saving ..." : "Save"}
					</Button>
				</form>
			)}
		</Form>
	)
}
